/**
 * Accounts — platform users known via federation.
 *
 * Backend: GET /api/v1/admin/accounts; POST /deactivate, /reactivate;
 * DELETE. Search and status filtering are client-side because the API
 * returns the full list; lift to server-side filters when the result
 * set grows past a few thousand rows.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { cloneElement, useMemo, useState, type ReactElement, type ReactNode } from 'react';

import { useRecordPanel } from '../../components/shell/record.js';
import { useOpenRecordPage } from '../../components/shell/record-page.js';
import { ApiError } from '@optum/api-client/client';
import {
  deactivateAccount,
  deleteAccount,
  listAccounts,
  reactivateAccount,
} from '@optum/api-client/endpoints/accounts';
import type { Account, AccountStatus } from '@optum/api-client/types';

import { useAuth } from '../../auth/AuthContext.js';
import { Button } from '../../ui/Button.js';
import { ChipFilter, ListToolbar } from '../../ui/ListToolbar.js';
import { EmptyBlock, ErrorBlock, LoadingBlock } from '../../ui/AsyncState.js';
import { StatusBadge } from '../../ui/StatusBadge.js';
import { useToast } from '../../ui/useToast.js';
import { qk } from '../../lib/query-keys.js';
import { useDebounce } from '../../lib/use-debounce.js';
import { useConfirm } from '../../ui/Confirm.js';
import { DataTable } from '../../ui/DataTable.js';
import { ListShape } from '../../ui/shapes.js';
import { Avatar, Pagination } from '../../ui/finixt.js';

type StatusKey = 'all' | AccountStatus;

const STATUS_FILTERS: ReadonlyArray<{ key: StatusKey; label: string }> = [
  { key: 'all', label: 'All' },
  { key: 'active', label: 'Active' },
  { key: 'inactive', label: 'Inactive' },
];

const PAGE_SIZE = 25;

export function AccountsPage() {
  const { isPlatformAdmin } = useAuth();
  const [status, setStatus] = useState<StatusKey>('all');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const debounced = useDebounce(search, 200);

  const query = useQuery({ queryKey: qk.accounts(), queryFn: listAccounts });

  const filtered = useMemo(
    () => filterRows(query.data ?? [], status, debounced),
    [query.data, status, debounced],
  );
  const pageRows = filtered.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);

  return (
    <ListShape
      title="Accounts"
      subtitle="Client users across every tenant. One row per identity, aggregated over its memberships."
    >
      <ListToolbar
        bare
        search={search}
        onSearch={(v) => {
          setSearch(v);
          setPage(0);
        }}
        placeholder="Search by email or external sub…"
        filters={
          <ChipFilter
            options={STATUS_FILTERS}
            value={status}
            onChange={(next) => {
              setStatus(next);
              setPage(0);
            }}
          />
        }
      />

      {query.isPending && <LoadingBlock />}
      {query.isError && (
        <ErrorBlock
          message={query.error instanceof Error ? query.error.message : 'Failed to load'}
        />
      )}
      {query.data && filtered.length === 0 && <EmptyBlock>No accounts match.</EmptyBlock>}
      {query.data && pageRows.length > 0 && (
        <AccountsTable
          rows={pageRows}
          canMutate={isPlatformAdmin}
          count={`${filtered.length} of ${query.data.length} accounts`}
        />
      )}
      {query.data && filtered.length > PAGE_SIZE && (
        <Pagination
          total={filtered.length}
          page={page + 1}
          pageSize={PAGE_SIZE}
          onChange={(next) => setPage(next - 1)}
        />
      )}
    </ListShape>
  );
}

function filterRows(rows: Account[], status: StatusKey, text: string): Account[] {
  const q = text.trim().toLowerCase();
  return rows.filter((a) => {
    if (status !== 'all' && a.status !== status) return false;
    if (!q) return true;
    return a.email.toLowerCase().includes(q) || a.external_sub.toLowerCase().includes(q);
  });
}

function AccountsTable({
  rows,
  canMutate,
  count,
}: {
  rows: Account[];
  canMutate: boolean;
  count: string;
}) {
  const panel = useRecordPanel();
  const openPage = useOpenRecordPage();
  return (
    <DataTable
      count={count}
      hint="group membership is per tenant"
      rows={rows}
      rowKey="external_sub"
      selectedKey={panel.current?.kind === 'account' ? panel.current.id : undefined}
      onRowClick={(account) => panel.open({ kind: 'account', id: account.external_sub })}
      onRowOpenPage={(account) => openPage({ kind: 'account', id: account.external_sub })}
      columns={[
        {
          key: 'email',
          label: 'Account',
          strong: true,
          render: (account) => (
            <div style={{ display: 'flex', alignItems: 'center', gap: 11 }}>
              <Avatar initials={initial(account.email)} size={32} />
              <span
                style={{
                  fontSize: 'var(--text-body-md)',
                  fontWeight: 'var(--weight-medium)',
                  color: 'var(--text-strong)',
                }}
              >
                {account.email}
              </span>
            </div>
          ),
        },
        {
          key: 'organizations',
          label: 'Organizations',
          align: 'right',
          mono: true,
          render: (account) => account.organizations.length,
        },
        {
          key: 'status',
          label: 'Status',
          render: (account) => <StatusBadge status={account.status} />,
        },
        {
          key: 'actions',
          label: 'Actions',
          align: 'right',
          stopRowClick: true,
          render: (account) => <AccountActions account={account} canMutate={canMutate} />,
        },
      ]}
    />
  );
}

function AccountActions({ account, canMutate }: { account: Account; canMutate: boolean }) {
  const qc = useQueryClient();
  const toast = useToast();

  const deactivate = useMutation({
    mutationFn: () => deactivateAccount(account.external_sub),
    onSuccess: () => {
      toast.success(`Deactivated ${account.email}`);
      qc.invalidateQueries({ queryKey: qk.accounts() });
    },
    onError: (e) => toast.error('Deactivate failed', e instanceof ApiError ? e.message : String(e)),
  });
  const reactivate = useMutation({
    mutationFn: () => reactivateAccount(account.external_sub),
    onSuccess: () => {
      toast.success(`Reactivated ${account.email}`);
      qc.invalidateQueries({ queryKey: qk.accounts() });
    },
    onError: (e) => toast.error('Reactivate failed', e instanceof ApiError ? e.message : String(e)),
  });
  const remove = useMutation({
    mutationFn: () => deleteAccount(account.external_sub),
    onSuccess: () => {
      toast.success(`Deleted ${account.email}`);
      qc.invalidateQueries({ queryKey: qk.accounts() });
    },
    onError: (e) => toast.error('Delete failed', e instanceof ApiError ? e.message : String(e)),
  });

  if (!canMutate) {
    return (
      <span aria-hidden="true" style={{ color: 'var(--text-muted)' }}>
        —
      </span>
    );
  }

  const isInactive = account.status === 'inactive';
  return (
    <div style={{ display: 'inline-flex', flexWrap: 'wrap', justifyContent: 'flex-end', gap: 7 }}>
      {!isInactive && (
        <ConfirmAction
          trigger={
            <Button size="sm" variant="danger" disabled={deactivate.isPending}>
              {deactivate.isPending ? 'Deactivating…' : 'Deactivate'}
            </Button>
          }
          title="Deactivate this account?"
          description={
            <>
              <strong>{account.email}</strong> will lose access to every organization. Live sessions
              are revoked at the IdP. This is reversible via Reactivate.
            </>
          }
          confirmLabel="Deactivate"
          onConfirm={() => deactivate.mutate()}
        />
      )}
      {isInactive && (
        <>
          <ConfirmAction
            trigger={
              <Button size="sm" variant="secondary" disabled={reactivate.isPending}>
                {reactivate.isPending ? 'Reactivating…' : 'Reactivate'}
              </Button>
            }
            title="Reactivate this account?"
            description={
              <>
                Restores every membership for <strong>{account.email}</strong> to{' '}
                <code>active</code>. The user can sign in again.
              </>
            }
            confirmLabel="Reactivate"
            onConfirm={() => reactivate.mutate()}
          />
          <ConfirmAction
            trigger={
              <Button size="sm" variant="danger" disabled={remove.isPending}>
                {remove.isPending ? 'Deleting…' : 'Delete'}
              </Button>
            }
            title="Delete this account permanently?"
            description={
              <>
                <strong>{account.email}</strong> will be hard-removed along with every membership
                row. The user may reappear via JIT on a future request, but their identity history
                is gone. This cannot be undone.
              </>
            }
            confirmLabel="Delete"
            onConfirm={() => remove.mutate()}
          />
        </>
      )}
    </div>
  );
}

interface ConfirmActionProps {
  trigger: ReactElement<{ onClick?: (() => void) | undefined }>;
  title: string;
  description: ReactNode;
  confirmLabel: string;
  onConfirm: () => void;
}

function ConfirmAction({
  trigger,
  title,
  description,
  confirmLabel,
  onConfirm,
}: ConfirmActionProps) {
  const confirm = useConfirm();
  return cloneElement(trigger, {
    onClick: () => {
      void confirm({ title, description, confirmLabel, tone: 'danger' }).then((ok) => {
        if (ok) onConfirm();
      });
    },
  });
}

function initial(email: string): string {
  return email.charAt(0).toUpperCase() || '·';
}
